'use client';

import React from 'react';
import * as RadixSelect from '@radix-ui/react-select';
import styled from 'styled-components';
import { focusRingInset } from '@liftango/ui-tokens';
import { Icon } from '@liftango/ui-icons';
import { useDensity } from '@liftango/ui-tokens';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

const Trigger = styled(RadixSelect.Trigger)<{ $height: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: ${(p) => p.theme.spaceAlias.sm};
  height: ${(p) => p.$height}px;
  min-width: 120px;
  padding: 0 ${(p) => p.theme.spaceAlias.sm} 0 ${(p) => p.theme.spaceAlias.md};
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.md};
  background: ${(p) => p.theme.colors.neutral.panel};
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.body};
  color: ${(p) => p.theme.colors.neutral.ink};
  cursor: pointer;
  ${focusRingInset}
  &[data-placeholder] {
    color: ${(p) => p.theme.colors.neutral.inkFaint};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Content = styled(RadixSelect.Content)`
  overflow: hidden;
  background: ${(p) => p.theme.colors.neutral.panel};
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.md};
  box-shadow: ${(p) => p.theme.elevation.overlay.shadow};
  z-index: ${(p) => p.theme.zIndex.popover};
  min-width: var(--radix-select-trigger-width);
`;

const Viewport = styled(RadixSelect.Viewport)`
  padding: 4px;
`;

const Item = styled(RadixSelect.Item)`
  display: flex;
  align-items: center;
  padding: 6px 8px 6px 24px;
  border-radius: ${(p) => p.theme.radius.sm};
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.body};
  color: ${(p) => p.theme.colors.neutral.ink};
  cursor: pointer;
  outline: none;
  position: relative;
  &[data-highlighted] {
    background: ${(p) => p.theme.colors.brand.primarySoft};
    color: ${(p) => p.theme.colors.brand.primary};
  }
`;

const ItemIndicator = styled(RadixSelect.ItemIndicator)`
  position: absolute;
  inset-inline-start: 4px;
  display: flex;
  align-items: center;
`;

export function Select({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  'aria-label': ariaLabel,
}: SelectProps) {
  const density = useDensity();
  return (
    <RadixSelect.Root value={value} onValueChange={onChange} disabled={disabled}>
      <Trigger aria-label={ariaLabel} $height={density.controlHeight}>
        <RadixSelect.Value placeholder={placeholder} />
        <RadixSelect.Icon aria-hidden>
          <Icon name="chevron-down" size={12} />
        </RadixSelect.Icon>
      </Trigger>
      <RadixSelect.Portal>
        <Content position="popper" sideOffset={4}>
          <Viewport>
            {options.map((opt) => (
              <Item key={opt.value} value={opt.value}>
                <ItemIndicator>
                  <Icon name="check" size={12} />
                </ItemIndicator>
                <RadixSelect.ItemText>{opt.label}</RadixSelect.ItemText>
              </Item>
            ))}
          </Viewport>
        </Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
